// React / Next
import { useRef, useState, useEffect } from 'react';
import { motion, Variants, useInView, AnimatePresence } from 'framer-motion';

// Components
import { ShowcaseCard } from './ShowcaseCards';
import { TextMaskReveal } from '../ui/TextMaskReveal/TextMaskReveal';

// Hooks
import { useShowcaseTimer } from './useShowcaseTimer';

// Types
import { SHOWCASE_SLIDES, SHOWCASE_CATEGORIES, ShowcaseCategory } from './types';

// Helper to get active category index based on current slide
const getActiveCategoryIndex = (slideIndex: number): number => {
  return SHOWCASE_CATEGORIES.findIndex(
    (cat) => slideIndex >= cat.slideRange[0] && slideIndex <= cat.slideRange[1]
  );
};

// Category Buttons Component
type CategoryButtonsProps = {
  activeSlideIndex: number;
  onCategoryClick: (slideIndex: number) => void;
};

const CategoryButtons = ({ activeSlideIndex, onCategoryClick }: CategoryButtonsProps) => {
  const activeCategoryIndex = getActiveCategoryIndex(activeSlideIndex);

  const handleClick = (category: ShowcaseCategory) => {
    // Jump to first slide of the category
    onCategoryClick(category.slideRange[0]);
  };

  const handleKeyDown = (e: React.KeyboardEvent, category: ShowcaseCategory) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(category);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-[16px]">
      {SHOWCASE_CATEGORIES.map((category, index) => {
        const isActive = index === activeCategoryIndex;

        return (
          <button
            key={category.id}
            onClick={() => handleClick(category)}
            onKeyDown={(e) => handleKeyDown(e, category)}
            className={`
              font-body text-[16px] leading-[24px] px-[16px] py-[12px] rounded-8
              transition-colors duration-200 cursor-pointer
              ${isActive
                ? 'bg-blue-1000 text-white'
                : 'bg-white border border-blue-1000 text-blue-1000 hover:bg-gray-50'
              }
            `}
            aria-label={`${category.label} - ${isActive ? 'Active category' : 'Click to view'}`}
            aria-pressed={isActive}
          >
            {category.label}
          </button>
        );
      })}
    </div>
  );
};

// Arrow Icons
const ArrowLeftIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.33333 15L3.33333 10M3.33333 10L8.33333 5M3.33333 10H16.6667"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowRightIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.6667 5L16.6667 10M16.6667 10L11.6667 15M16.6667 10H3.33333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Animation variants for content transitions
const contentVariants: Variants = {
  initial: {
    opacity: 0,
    x: 20,
  },
  animate: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

export const ArchieShowcaseSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track when section is in view (once: true means it stays true after first trigger)
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  // Timer hook for auto-advance and progress
  const { state, pause, play, goToSlide, goToNext, goToPrevious } = useShowcaseTimer({ isInView });

  // Track if a "jump" just happened (slide change) to apply elastic animation
  const [isJumping, setIsJumping] = useState(false);
  const prevSlideRef = useRef(state.slideIndex);

  // Detect slide changes and trigger elastic animation
  useEffect(() => {
    if (prevSlideRef.current !== state.slideIndex) {
      setIsJumping(true);
      prevSlideRef.current = state.slideIndex;

      // Reset after the spring animation completes (~500ms)
      const timer = setTimeout(() => setIsJumping(false), 500);
      return () => clearTimeout(timer);
    }
  }, [state.slideIndex]);

  // Get current slide data
  const currentSlide = SHOWCASE_SLIDES[state.slideIndex];

  // Pause on hover, resume on leave
  const handleMouseEnter = () => pause();
  const handleMouseLeave = () => play();

  return (
    <section
      ref={sectionRef}
      id="use-cases"
      className="flex flex-col items-center gap-[var(--spacing-48)] px-[var(--spacing-24)] py-[var(--spacing-96)] w-full max-w-[1224px] max-md:px-[var(--spacing-16)] max-md:py-[var(--spacing-64)]"
    >
      {/* Section Title */}
      <TextMaskReveal
        variant="h1"
        className="text-blue-1000 text-center"
        animateOnMount={false}
        // triggerOnView
        splitByVisualLines
        staggerDelay={0.08}
        duration={0.5}
      >
        How Accounting Teams Work With Archie
      </TextMaskReveal>

      {/* Category Buttons */}
      <CategoryButtons activeSlideIndex={state.slideIndex} onCategoryClick={goToSlide} />

      {/* Showcase Card */}
      <motion.div
        ref={containerRef}
        className="relative w-full max-w-[1153px] rounded-[var(--radius-24)] border border-gray-200 overflow-hidden bg-blue-200"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        initial={{ opacity: 0, y: 40, scale: 0.98 }}
        animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 40, scale: 0.98 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        {/* Progress Bar with Timer Animation */}
        <motion.div
          className="absolute top-0 left-0 h-[7px] bg-blue-1000 z-10"
          initial={{ width: 0 }}
          animate={{ width: `${state.progress}%` }}
          transition={
            isJumping
              ? {
                type: 'spring',
                stiffness: 120,
                damping: 14,
                mass: 0.6,
              }
              : {
                duration: 0.1,
                ease: 'linear',
              }
          }
        />

        {/* Content Container */}
        <div className="flex min-h-[600px] max-lg:flex-col">
          {/* Left Side - Blue with Card */}
          <div className="flex-1 flex items-center justify-center p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={`card-${currentSlide.id}`}
                className="w-full max-w-[488px]"
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ShowcaseCard cardType={currentSlide.cardType} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Right Side - Gray with Text */}
          <div className="flex-1 flex items-center justify-center p-8 max-lg:py-12 bg-gray-50">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide.id}
                className="flex flex-col gap-4 max-w-[463px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <TextMaskReveal
                  key={`title-${currentSlide.id}`}
                  variant="h2"
                  className="text-blue-1000"
                  animateOnMount
                  splitByVisualLines
                  staggerDelay={0.08}
                  duration={0.5}
                >
                  {currentSlide.title}
                </TextMaskReveal>
                <TextMaskReveal
                  key={`desc-${currentSlide.id}`}
                  className="font-body text-[16px] leading-[24px] text-blue-1000"
                  animateOnMount
                  splitByVisualLines
                  delay={0.25}
                  staggerDelay={0.06}
                  duration={0.45}
                >
                  {currentSlide.description}
                </TextMaskReveal>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Arrows - Desktop: absolute at bottom. Mobile: in flow below text */}
          <div className="absolute bottom-6 left-6 right-6 hidden lg:flex items-center justify-between z-20">
            <button
              onClick={goToPrevious}
              className="p-[8px] rounded-[24px] border border-blue-1000 bg-white flex items-center justify-center text-blue-1000 hover:bg-gray-50 transition-colors"
              aria-label="Previous slide"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>

            <button
              onClick={goToNext}
              className="p-[8px] rounded-[24px] border border-blue-1000 bg-white flex items-center justify-center text-blue-1000 hover:bg-gray-50 transition-colors"
              aria-label="Next slide"
            >
              <ArrowRightIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation Arrows - Mobile: below content so they don't overlap text */}
        <div className="flex lg:hidden items-center justify-between px-6 pb-6 pt-4 bg-gray-50">
          <button
            onClick={goToPrevious}
            className="p-[8px] rounded-[24px] border border-blue-1000 bg-white flex items-center justify-center text-blue-1000 hover:bg-gray-50 transition-colors"
            aria-label="Previous slide"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>

          <button
            onClick={goToNext}
            className="p-[8px] rounded-[24px] border border-blue-1000 bg-white flex items-center justify-center text-blue-1000 hover:bg-gray-50 transition-colors"
            aria-label="Next slide"
          >
            <ArrowRightIcon className="w-5 h-5" />
          </button>
        </div>
      </motion.div>
    </section>
  );
};
